import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowRight, Loader2 } from "lucide-react";
import { submitContact } from "@/lib/api";
import { useReveal } from "@/hooks/useReveal";
import { IMG } from "@/lib/images";
import { INTEREST_OPTIONS } from "@/data/services";

const CONTACT_IMG = IMG.cornerSky;

const initial = {
    name: "",
    email: "",
    company: "",
    role: "",
    interest: "",
    message: "",
};

export default function Contact() {
    useReveal();
    const [form, setForm] = useState(initial);
    const [busy, setBusy] = useState(false);
    const [sent, setSent] = useState(false);
    const [searchParams] = useSearchParams();

    // Preselecciona el servicio de interés cuando se llega desde un CTA (?interes=...)
    useEffect(() => {
        const p = searchParams.get("interes");
        if (p && INTEREST_OPTIONS.some((o) => o.value === p)) {
            setForm((f) => ({ ...f, interest: p }));
        }
    }, [searchParams]);

    const change = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

    const submit = async (e) => {
        e.preventDefault();
        if (!form.name.trim() || !form.email.trim() || !form.message.trim()) {
            toast.error("Nombre, email y mensaje son obligatorios.");
            return;
        }
        setBusy(true);
        try {
            await submitContact(form);
            setSent(true);
            setForm(initial);
            toast.success("Mensaje recibido. Un partner te contactará en menos de 24 horas hábiles.");
        } catch (err) {
            const detail = err?.response?.data?.detail;
            toast.error(
                typeof detail === "string"
                    ? detail
                    : "No se pudo enviar ahora mismo. Inténtalo de nuevo."
            );
        } finally {
            setBusy(false);
        }
    };

    return (
        <div data-testid="contact-page">
            {/* Hero */}
            <section className="container-x pt-16 pb-12 lg:pt-24 lg:pb-16 border-b border-brand-line">
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 reveal">
                    <div className="lg:col-span-8">
                        <div className="label-mono">Contacto · Colaboraciones</div>
                        <h1 className="mt-8 font-heading text-5xl lg:text-7xl leading-[0.98] tracking-tighter text-brand-navy">
                            Empieza una <span className="italic text-brand-electric">conversación de trabajo.</span>
                        </h1>
                    </div>
                    <div className="lg:col-span-4">
                        <p className="text-brand-gray text-lg leading-relaxed">
                            Cuéntanos qué quieres lograr en tu negocio y te
                            responderemos con una perspectiva de socio, no con una
                            respuesta automática.
                        </p>
                    </div>
                </div>
            </section>

            {/* Form + Side */}
            <section className="container-x py-16 lg:py-24">
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20">
                    {/* Form */}
                    <div className="lg:col-span-7 reveal">
                        {sent ? (
                            <div
                                className="border border-brand-line p-10"
                                data-testid="contact-success"
                            >
                                <div className="label-mono">Recibido · 200 OK</div>
                                <h2 className="font-heading text-3xl mt-6 leading-tight text-brand-navy">
                                    Mensaje recibido.
                                </h2>
                                <p className="mt-4 text-brand-gray">
                                    Un socio de Scalexa responderá en menos de 24
                                    horas hábiles. Mientras tanto, puedes hacer el
                                    diagnóstico de Crecimiento con IA para adelantar
                                    la conversación.
                                </p>
                                <button
                                    type="button"
                                    onClick={() => setSent(false)}
                                    className="btn-ghost mt-8"
                                    data-testid="contact-send-another"
                                >
                                    Enviar otro mensaje
                                </button>
                            </div>
                        ) : (
                            <form onSubmit={submit} className="space-y-8" data-testid="contact-form">
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                                    <div>
                                        <label className="label-mono block mb-2">01, Nombre *</label>
                                        <input
                                            data-testid="contact-input-name"
                                            value={form.name}
                                            onChange={change("name")}
                                            className="input-line"
                                            placeholder="Tu nombre completo"
                                            required
                                        />
                                    </div>
                                    <div>
                                        <label className="label-mono block mb-2">02, Email *</label>
                                        <input
                                            data-testid="contact-input-email"
                                            type="email"
                                            value={form.email}
                                            onChange={change("email")}
                                            className="input-line"
                                            placeholder="tu@empresa.com"
                                            required
                                        />
                                    </div>
                                    <div>
                                        <label className="label-mono block mb-2">03, Empresa</label>
                                        <input
                                            data-testid="contact-input-company"
                                            value={form.company}
                                            onChange={change("company")}
                                            className="input-line"
                                            placeholder="Dónde operas"
                                        />
                                    </div>
                                    <div>
                                        <label className="label-mono block mb-2">04, Rol</label>
                                        <input
                                            data-testid="contact-input-role"
                                            value={form.role}
                                            onChange={change("role")}
                                            className="input-line"
                                            placeholder="CEO, CTO, COO, CRO…"
                                        />
                                    </div>
                                </div>
                                <div>
                                    <label className="label-mono block mb-2" htmlFor="contact-interest">
                                        05, Servicio de interés
                                    </label>
                                    <select
                                        id="contact-interest"
                                        data-testid="contact-select-interest"
                                        value={form.interest}
                                        onChange={change("interest")}
                                        className="input-line bg-transparent w-full"
                                    >
                                        <option value="">Selecciona una opción (opcional)</option>
                                        {INTEREST_OPTIONS.map((o) => (
                                            <option key={o.value} value={o.value}>
                                                {o.label}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                                <div>
                                    <label className="label-mono block mb-2">06, ¿Qué quieres lograr? *</label>
                                    <textarea
                                        data-testid="contact-input-message"
                                        value={form.message}
                                        onChange={change("message")}
                                        className="input-line min-h-[120px] resize-none"
                                        placeholder="Crecimiento, ventas, eficiencia, equipo… cuéntanos tu objetivo de negocio."
                                        rows={5}
                                        required
                                    />
                                </div>
                                <button
                                    type="submit"
                                    disabled={busy}
                                    data-testid="contact-submit"
                                    className="btn-primary disabled:opacity-50"
                                >
                                    {busy ? (
                                        <>
                                            <Loader2 className="animate-spin" size={16} /> Enviando
                                        </>
                                    ) : (
                                        <>
                                            Enviar a un partner <ArrowRight size={16} />
                                        </>
                                    )}
                                </button>
                            </form>
                        )}
                    </div>

                    {/* Side info */}
                    <aside className="lg:col-span-5 reveal">
                        <div className="aspect-[4/5] relative overflow-hidden mb-10">
                            <img
                                src={CONTACT_IMG}
                                alt="Oficina de Scalexa Group en Madrid — consultoría empresarial, IA y aceleración comercial"
                                className="absolute inset-0 w-full h-full object-cover"
                            />
                            <div className="absolute inset-0 border border-brand-line/80" />
                        </div>
                        <div className="border-t border-brand-line divide-y divide-brand-line">
                            <div className="py-5">
                                <div className="label-mono">Directo</div>
                                <div className="mt-2 text-brand-navy">partners@scalexa-group.com</div>
                            </div>
                            <div className="py-5">
                                <div className="label-mono">Oficina</div>
                                <div className="mt-2 text-brand-navy">
                                    Calle López de Hoyos 42<br />
                                    Entrada por Edison, 3<br />
                                    28006 Madrid, España
                                </div>
                            </div>
                            <div className="py-5">
                                <div className="label-mono">Presencia</div>
                                <div className="mt-2 text-brand-navy">Madrid · Ciudad de México</div>
                            </div>
                            <div className="py-5">
                                <div className="label-mono">Ventana de respuesta</div>
                                <div className="mt-2 text-brand-navy">≤ 24 horas hábiles</div>
                            </div>
                        </div>
                    </aside>
                </div>
            </section>
        </div>
    );
}
